import { useEffect } from 'react';

import { telemetryAreas, type TelemetryArea } from '../shared/telemetry-catalog';
import { reportScreen } from './telemetry';

/**
 * The area in front (telemetry catalog `telemetryAreas`, ADR-0081 amendment 2026-10-06): one code name for where the
 * person is, finer than the screen. Each part of the window says its own piece (App the screen and the job's mode, Job
 * setup its section, Export its stop, Learning its stage, the work screen what it shows) and the name is put together
 * from all of them here, so it never depends on which of them rendered last. Only code words go in: section ids, stop
 * ids, tab ids, never a name.
 */
export type TelemetryAreaParts = {
  /** App: the screen, and whether this computer has no jobs yet (the welcome). */
  screen: 'jobs' | 'job' | 'settings' | 'cluster' | 'learning';
  noJobs: boolean;
  /** App: the job's mode as shown (the setup wait is setup), the Settings section, whether a job is open (unknown while jobs load). */
  jobMode: 'capture' | 'setup' | 'export';
  settingsSection: string;
  hasJob: boolean;
  /** The work screen: what it shows, what the documents list asks about (Calibration or review), a tour running on this job, the separator sheets window. */
  capture: 'documents' | 'reading' | 'setup-wait' | 'start' | 'caught-up';
  documents: 'review' | 'calibration';
  tour: boolean;
  breakSheets: boolean;
  /** Job setup: its first setup's wait, or the section; and the set-up wizard held over the work screen. */
  setup:
    | 'wait'
    | 'document-types'
    | 'document-starts'
    | 'thresholds'
    | 'learning'
    | 'lookups'
    | 'watched-folder'
    | 'export';
  setupWizard: boolean;
  /** Learning's stage, in Job setup and on the Learning screen (two can be open at once). */
  setupLearning: 'teach' | 'train' | 'results';
  learning: 'teach' | 'train' | 'results';
  /** Export: the stop, and a window over it. */
  exportStop: 'when' | 'files' | 'destination' | 'backup' | 'elsewhere' | 'exceptions' | 'activity';
  exportWindow: 'naming' | 'content-central';
};
export type TelemetryAreaState = { [K in keyof TelemetryAreaParts]?: TelemetryAreaParts[K] | null };

const listed = (name: string): TelemetryArea | null =>
  (telemetryAreas as readonly string[]).includes(name) ? (name as TelemetryArea) : null;

/** The area these parts make, or null while it is not known yet (a part still to say its piece). Pure. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function telemetryAreaName(parts: TelemetryAreaState): TelemetryArea | null {
  switch (parts.screen) {
    case 'jobs':
      return parts.noJobs ? 'welcome' : 'jobs';
    case 'settings':
      return parts.settingsSection ? listed(`settings/${parts.settingsSection}`) : null;
    case 'cluster':
      return 'cluster';
    case 'learning':
      return parts.learning ? listed(`learning/${parts.learning}`) : null;
    case 'job':
      break;
    default:
      return null;
  }
  if (parts.noJobs) return 'welcome';
  if (parts.hasJob === undefined || parts.hasJob === null) return null;
  if (!parts.hasJob) return 'jobs';
  // A tour runs over the real screens of its job: its time there is the tour's.
  if (parts.tour) return 'tour';
  if (parts.jobMode === 'setup' || parts.jobMode === 'export') {
    const section = parts.jobMode === 'export' ? 'export' : parts.setup;
    if (!section) return null;
    if (section === 'wait') return 'job/setup/wait';
    if (section === 'learning')
      return parts.setupLearning ? listed(`job/setup/learning/${parts.setupLearning}`) : null;
    if (section === 'export') {
      if (parts.exportWindow) return listed(`job/setup/export/${parts.exportWindow}`);
      return parts.exportStop ? listed(`job/setup/export/${parts.exportStop}`) : null;
    }
    return listed(`job/setup/${section}`);
  }
  if (parts.setupWizard) return 'job/setup/wait';
  if (parts.breakSheets) return 'job/break-sheets';
  switch (parts.capture) {
    case 'documents':
      return parts.documents ? listed(`job/documents/${parts.documents}`) : null;
    case 'setup-wait':
      return 'job/setup/wait';
    case 'reading':
      return 'job/reading';
    case 'start':
      return 'job/start';
    case 'caught-up':
      return 'job/caught-up';
    default:
      return null;
  }
}

const parts: TelemetryAreaState = {};
let pending: ReturnType<typeof setTimeout> | null = null;

/**
 * One part's piece of the area. Unchanged pieces cost a comparison. A change is reported a quarter second later, once
 * every part of that change has said its piece, so a step through on the way (the screen changed, the section not yet)
 * is not counted as a visit. Fire-and-forget.
 */
export function reportAreaPart<K extends keyof TelemetryAreaParts>(
  key: K,
  value: TelemetryAreaParts[K] | null | undefined,
): void {
  const next = value ?? null;
  if ((parts[key] ?? null) === next) return;
  parts[key] = next;
  if (pending !== null) return;
  pending = setTimeout(() => {
    pending = null;
    try {
      const area = telemetryAreaName(parts);
      if (area) reportScreen(area);
    } catch {
      /* telemetry never breaks a screen */
    }
  }, 250);
}

/** A component's piece of the area while it is mounted; it is taken back when the component goes. */
export function useTelemetryArea<K extends keyof TelemetryAreaParts>(
  key: K,
  value: TelemetryAreaParts[K] | null | undefined,
): void {
  useEffect(() => {
    reportAreaPart(key, value);
  }, [key, value]);
  useEffect(() => () => reportAreaPart(key, null), [key]);
}
